import React, { useCallback, useState } from "react";
import { Range } from "rc-slider";
import "rc-slider/assets/index.css";
import {
  Button,
  Col,
  ControlLabel,
  FormControl,
  FormGroup,
  Grid,
  OverlayTrigger,
  Popover,
  Row,
} from "react-bootstrap";
import { FormattedNumber } from "react-intl";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { submitFilter } from "../../../actions/index";
import { useLogarithmicFilter } from "../hooks";
import { PageControls } from "./page-controls";
import { selectSubscribed } from "../../../selectors";

const FlexDiv = styled.div`
  display: flex;
  justify-content: space-between;
`;

const CentredContentRow = styled(Row)`
  @media (min-width: 992px) {
    display: flex;
    align-items: center;
  }
`;

const COUNTRIES = [
  "USA",
  "Canada",
  "Australia",
  "Austria",
  "Belgium",
  "British",
  "Dutch",
  "France",
  "Germany",
  "HongKong",
  "India",
  "Italy",
  "Japan",
  "Mexico",
  "Spain",
  "Swedish",
  "Swiss",
];

const ORDER_OPTIONS = [
  {
    value: "latest_fee",
    label: "Fee",
  },
  {
    value: "latest_available",
    label: "Available",
  },
  {
    value: "latest_market_cap",
    label: "Market Cap",
  },
];

const DESC = "desc";
const ASC = "asc";

const ORDERING = [
  {
    value: ASC,
    label: "Ascending",
  },
  {
    value: DESC,
    label: "Descending",
  },
];

const MarketCapWarning = (
  <Popover id="market-cap-warning" title="USA only">
    Market cap filtering is only available when searching for US securities.
  </Popover>
);

export const StockFilter = () => {
  const subscribed = useSelector(selectSubscribed);
  const currentPage = useSelector(
    (state) => state.filteredStocks?.currentPage ?? 0
  );
  const hasNextPage = useSelector(
    (state) => state.filteredStocks?.hasNextPage ?? false
  );
  const hasPreviousPage = useSelector(
    (state) => state.filteredStocks?.hasPreviousPage ?? false
  );
  const [touched, setTouched] = useState(false);
  const triggerTouched = useCallback(() => {
    setTouched(true);
  }, [setTouched]);
  const [orderBy, setOrderBy] = useState(ORDER_OPTIONS[0].value);
  const onChangeOrderBy = useCallback(
    (event) => {
      setOrderBy(event.target.value);
      triggerTouched();
    },
    [setOrderBy]
  );

  const [ordering, setOrdering] = useState(ASC);
  const onChangeOrdering = useCallback(
    (event) => {
      setOrdering(event.target.value);
      triggerTouched();
    },
    [setOrdering]
  );

  const [country, setCountry] = useState("usa");
  const onChangeCountry = useCallback(
    (event) => {
      const newCountry = event.target.value;
      setCountry(newCountry);
      if (newCountry !== "usa" && orderBy === "latest_market_cap") {
        setOrderBy("latest_fee");
      }
      triggerTouched();
    },
    [setCountry, orderBy, setOrderBy]
  );

  const usaNotSelected = country !== "usa";

  const {
    min: minFee,
    max: maxFee,
    step: stepFee,
    onChange: onChangeFee,
    value: [minFeeValue, maxFeeValue],
  } = useLogarithmicFilter(0.1, 1000, triggerTouched);

  const {
    min: minAvailable,
    max: maxAvailable,
    step: stepAvailable,
    onChange: onChangeAvailable,
    value: [minAvailableValue, maxAvailableValue],
  } = useLogarithmicFilter(100, 10000000, triggerTouched);

  const {
    min: minMarketCap,
    max: maxMarketCap,
    step: stepMarketCap,
    onChange: onChangeMarketCap,
    value: [minMarketCapValue, maxMarketCapValue],
  } = useLogarithmicFilter(1000000, 1000000000000, triggerTouched);

  const dispatch = useDispatch();
  const onSubmit = useCallback(
    (extraParams = {}) => {
      const params = {
        min_available: minAvailableValue,
        max_available: maxAvailableValue,
        min_fee: minFeeValue,
        max_fee: maxFeeValue,
        country: country,
        sort: `${ordering === DESC ? "-" : ""}${orderBy}`,
        ...extraParams,
      };
      const marketCapParams = usaNotSelected
        ? {}
        : {
            min_market_cap: minMarketCapValue,
            max_market_cap: maxMarketCapValue,
          };
      dispatch(submitFilter({ ...params, ...marketCapParams }));
      setTouched(false);
    },
    [
      minFeeValue,
      maxFeeValue,
      minAvailableValue,
      maxAvailableValue,
      minMarketCapValue,
      maxMarketCapValue,
      country,
      orderBy,
      ordering,
      dispatch,
      setTouched,
    ]
  );
  const onClickSubmit = useCallback(() => {
    onSubmit();
  }, [onSubmit]);

  const onClickNext = useCallback(() => {
    onSubmit({ page: currentPage + 1 });
  }, [currentPage, onSubmit]);

  const onClickPrevious = useCallback(() => {
    onSubmit({ page: currentPage - 1 });
  }, [currentPage, onSubmit]);

  const orderOptions = ORDER_OPTIONS.filter(({ value }) => {
    if (value === "latest_market_cap" && usaNotSelected) return false;
    return true;
  });

  return (
    <Grid>
      <Row>
        <Col xs={12} md={4}>
          <FormGroup>
            <ControlLabel>Fee</ControlLabel>
            <FlexDiv style={{ marginBottom: "10px" }}>
              <div>
                <FormattedNumber
                  style="percent"
                  value={minFeeValue / 100}
                  maximumFractionDigits={1}
                />
              </div>
              <div>
                <FormattedNumber
                  style="percent"
                  value={maxFeeValue / 100}
                  maximumFractionDigits={1}
                />
              </div>
            </FlexDiv>
            <Range
              dots
              min={minFee}
              max={maxFee}
              defaultValue={[minFee, maxFee]}
              allowCross={false}
              step={stepFee}
              marks={[]}
              onChange={onChangeFee}
            />
          </FormGroup>
        </Col>
        <Col xs={12} md={4}>
          <FormGroup>
            <ControlLabel>Available</ControlLabel>
            <FlexDiv style={{ marginBottom: "10px" }}>
              <div>
                <FormattedNumber
                  value={minAvailableValue}
                  maximumFractionDigits={0}
                />
              </div>
              <div>
                <FormattedNumber
                  value={maxAvailableValue}
                  maximumFractionDigits={0}
                />
              </div>
            </FlexDiv>
            <Range
              dots
              min={minAvailable}
              max={maxAvailable}
              defaultValue={[minAvailable, maxAvailable]}
              allowCross={false}
              step={stepAvailable}
              marks={[]}
              onChange={onChangeAvailable}
            />
          </FormGroup>
        </Col>
        <Col xs={12} md={4}>
          <OverlayTrigger
            trigger={usaNotSelected ? ["hover", "focus"] : undefined}
            placement="top"
            overlay={MarketCapWarning}
          >
            <FormGroup>
              <ControlLabel>Market Cap ($mm)</ControlLabel>
              <FlexDiv style={{ marginBottom: "10px" }}>
                <div>
                  <FormattedNumber
                    style="currency"
                    currency="USD"
                    value={minMarketCapValue / 1000000}
                  />
                </div>
                <div>
                  <FormattedNumber
                    style="currency"
                    currency="USD"
                    value={maxMarketCapValue / 1000000}
                  />
                </div>
              </FlexDiv>
              <Range
                disabled={usaNotSelected}
                dots
                min={minMarketCap}
                max={maxMarketCap}
                defaultValue={[minMarketCap, maxMarketCap]}
                allowCross={false}
                step={stepMarketCap}
                marks={[]}
                onChange={onChangeMarketCap}
              />
            </FormGroup>
          </OverlayTrigger>
        </Col>
      </Row>
      <CentredContentRow>
        <Col xs={12} md={3}>
          <FormGroup>
            <ControlLabel>Country</ControlLabel>
            <FormControl
              componentClass="select"
              value={country}
              onChange={onChangeCountry}
            >
              {COUNTRIES.map((countryOption) => (
                <option value={countryOption.toLowerCase()} key={countryOption}>
                  {countryOption}
                </option>
              ))}
            </FormControl>
          </FormGroup>
        </Col>
        <Col xs={12} md={3}>
          <FormGroup>
            <ControlLabel>Order By</ControlLabel>
            <FormControl
              componentClass="select"
              value={orderBy}
              onChange={onChangeOrderBy}
            >
              {orderOptions.map(({ value, label }) => (
                <option value={value} key={value}>
                  {label}
                </option>
              ))}
            </FormControl>
          </FormGroup>
        </Col>
        <Col xs={12} md={3}>
          <FormGroup>
            <ControlLabel>Ordering</ControlLabel>
            <FormControl
              componentClass="select"
              value={ordering}
              onChange={onChangeOrdering}
            >
              {ORDERING.map(({ value, label }) => (
                <option value={value} key={value}>
                  {label}
                </option>
              ))}
            </FormControl>
          </FormGroup>
        </Col>
        <Col xs={12} md={3}>
          <Button bsStyle="primary" block onClick={onClickSubmit}>
            Submit
          </Button>
        </Col>
      </CentredContentRow>
      {subscribed && !touched && (hasPreviousPage || hasNextPage) && (
        <PageControls
          hasNextPage={hasNextPage}
          hasPreviousPage={hasPreviousPage}
          onClickNext={onClickNext}
          onClickPrevious={onClickPrevious}
        />
      )}
    </Grid>
  );
};
